import { useState } from 'react';
import { comics } from '@/mocks/comics';
import { Link } from 'react-router-dom';
import HeroSection from './components/HeroSection';
import HotRecommendSection from './components/HotRecommendSection';
import CategoryNavSection from './components/CategoryNavSection';
import ReviewSection from './components/ReviewSection';
import AboutPreviewSection from './components/AboutPreviewSection';
import CTASection from './components/CTASection';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { comicCoverImages } from '@/mocks/coverImages';

export default function Home() {
  const [activeTab, setActiveTab] = useState<'latest' | 'popular'>('latest');

  const sortedComics = activeTab === 'latest'
    ? [...comics].sort((a, b) => b.updateDate.localeCompare(a.updateDate))
    : [...comics].sort((a, b) => b.totalViews - a.totalViews);

  const displayComics = sortedComics.slice(0, 8);

  return (
    <>
      <SEOHead
        title="韩漫漫画网站-免费韩漫,漫画大全,在线漫画,好看的韩国漫画"
        description="韩漫漫画网站是免费韩漫与漫画大全的宝库，提供海量在线漫画资源，并持续为您挖掘和推荐好看的韩国漫画。涵盖恋爱、奇幻、动作、悬疑等多种题材，每日更新热门连载。"
        keywords="韩漫,免费韩漫,漫画大全,在线漫画,韩国漫画,好看的韩漫,韩漫在线阅读"
        canonicalUrl="/"
      />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="WebPage" data={{
        name: '韩漫漫画 - 免费在线韩国漫画阅读平台',
        description: '韩漫漫画网站是免费韩漫与漫画大全的宝库，提供海量在线漫画资源。',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'http://netkqn.com/' },
          ],
        },
      }} />

      <Navbar />
      <main>
        <HeroSection />
        <HotRecommendSection />
        <CategoryNavSection />

        <section id="latest" className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="mx-auto max-w-7xl">
              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
                <div>
                  <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                    漫画大全
                  </h2>
                  <p className="mt-2 text-foreground-500 text-sm">海量漫画资源，持续更新中</p>
                </div>
                <div className="flex items-center gap-2 bg-background-100 rounded-full p-1">
                  <button
                    type="button"
                    onClick={() => setActiveTab('latest')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeTab === 'latest'
                        ? 'bg-background-50 text-foreground-950'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    最新更新
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('popular')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeTab === 'popular'
                        ? 'bg-background-50 text-foreground-950'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    人气最高
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                {displayComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/comic/${comic.slug}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-xl bg-background-100 aspect-[3/4] mb-3">
                      <img
                        src={comicCoverImages[comic.slug]}
                        alt={comic.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                        title={comic.title}
                      />
                      <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-primary-500 text-background-50 text-xs font-medium">
                        {comic.rating}
                      </div>
                      {comic.status === '完结' && (
                        <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-foreground-950/60 text-background-50 text-xs">
                          已完结
                        </div>
                      )}
                    </div>
                    <h3 className="text-foreground-950 text-sm font-medium truncate group-hover:text-primary-500 transition-colors">
                      {comic.title}
                    </h3>
                    <p className="text-foreground-500 text-xs mt-1">{comic.updatedChapter}</p>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {comic.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </Link>
                ))}
              </div>

              <div className="mt-10 text-center">
                <Link
                  to="/category"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-foreground-950 hover:bg-foreground-800 text-background-50 font-medium text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer"
                >
                  浏览全部漫画 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <ReviewSection />
        <AboutPreviewSection />
        <CTASection />
      </main>
      <Footer />
    </>
  );
}